import {
	AlertCircleIcon,
	AlertDiamondIcon,
	Search01Icon,
	Tick02Icon,
	Time04Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, router } from "@inertiajs/react";
import type { VariantProps } from "class-variance-authority";
import { useState } from "react";
import { CheckStatusBadge } from "@/components/check-status-badge";
import Dashboard from "@/components/dashboard";
import { NewClientButton } from "@/components/new-client-button";
import { Badge, type badgeVariants } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { VerificationStatusBadge } from "@/components/verification-status-badge";
import { formatBirthNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type ClientsClient from "@/types/serializers/ClientsClient";
import type HomeUser from "@/types/serializers/HomeUser";

interface Props {
	user: HomeUser;
	clients: ClientsClient[];
	filters: {
		search?: string;
		type?: string;
		verification_status?: string;
		check_status?: string;
	};
}

const typeFilterItems = [
	{ value: null, label: "Všechny typy" },
	{ value: "IndividualClient", label: "Fyzická osoba" },
	{ value: "IndividualEntrepreneurClient", label: "Fyzická osoba (OSVČ)" },
	{ value: "BusinessClient", label: "Právnická osoba" },
];

const verificationStatusFilterItems: {
	value: string | null;
	label: string;
	variant?: VariantProps<typeof badgeVariants>["variant"];
	icon?: typeof Tick02Icon;
}[] = [
	{ value: null, label: "Všechny stavy identifikace" },
	{ value: "verified", label: "Ověřeno", variant: "default", icon: Tick02Icon },
	{
		value: "partially_verified",
		label: "Částečně ověřeno",
		variant: "outline",
	},
	{ value: "not_verified", label: "Neověřeno", variant: "secondary" },
	{
		value: "expiring_soon",
		label: "Expiruje brzy",
		variant: "warning",
		icon: Time04Icon,
	},
	{
		value: "expired",
		label: "Expirováno",
		variant: "destructive",
		icon: AlertDiamondIcon,
	},
];

const checkStatusFilterItems: {
	value: string | null;
	label: string;
	variant?: VariantProps<typeof badgeVariants>["variant"];
	icon?: typeof Tick02Icon;
}[] = [
	{ value: null, label: "Všechny stavy kontrol" },
	{
		value: "checked",
		label: "Zkontrolováno",
		variant: "default",
		icon: Tick02Icon,
	},
	{ value: "not_checked", label: "Nezkontrolováno", variant: "secondary" },
	{
		value: "expiring_soon",
		label: "Expiruje brzy",
		variant: "warning",
		icon: Time04Icon,
	},
	{
		value: "expired",
		label: "Expirováno",
		variant: "destructive",
		icon: AlertDiamondIcon,
	},
];

type ClientableType = ClientsClient["clientable_type"];

const CLIENTABLE_TYPE_LABELS = {
	IndividualClient: "Fyzická osoba",
	IndividualEntrepreneurClient: "Fyzická osoba (OSVČ)",
	BusinessClient: "Právnická osoba",
} satisfies Record<ClientableType, string>;

export default function ClientsIndex({ user, clients, filters }: Props) {
	const [search, setSearch] = useState(filters.search || "");

	const handleSearch = (value: string) => {
		setSearch(value);
		router.get(
			"/clients",
			{
				search: value || undefined,
				type: filters.type || undefined,
				verification_status: filters.verification_status || undefined,
				check_status: filters.check_status || undefined,
			},
			{ preserveState: true, replace: true },
		);
	};

	const handleTypeFilter = (value: string | null) => {
		router.get(
			"/clients",
			{
				search: filters.search || undefined,
				type: value || undefined,
				verification_status: filters.verification_status || undefined,
				check_status: filters.check_status || undefined,
			},
			{ preserveState: true, replace: true },
		);
	};

	const handleVerificationStatusFilter = (value: string | null) => {
		router.get(
			"/clients",
			{
				search: filters.search || undefined,
				type: filters.type || undefined,
				verification_status: value || undefined,
				check_status: filters.check_status || undefined,
			},
			{ preserveState: true, replace: true },
		);
	};

	const handleCheckStatusFilter = (value: string | null) => {
		router.get(
			"/clients",
			{
				search: filters.search || undefined,
				type: filters.type || undefined,
				verification_status: filters.verification_status || undefined,
				check_status: value || undefined,
			},
			{ preserveState: true, replace: true },
		);
	};

	return (
		<>
			<Head title="Klienti" />
			<Dashboard user={user}>
				<div className="flex flex-col gap-4 p-4 lg:p-6">
					<div className="flex items-center justify-between">
						<h2 className="font-semibold text-2xl">Klienti</h2>
						<NewClientButton />
					</div>

					<div className="flex items-center gap-4">
						<div className="relative flex-1">
							<HugeiconsIcon
								icon={Search01Icon}
								strokeWidth={2}
								className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
							/>
							<Input
								placeholder="Hledat..."
								value={search}
								onChange={(e) => handleSearch(e.target.value)}
								className="pl-8"
							/>
						</div>
						<Select
							value={filters.type}
							onValueChange={handleTypeFilter}
							items={typeFilterItems}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="min-w-fit" align="end">
								{typeFilterItems.map((item) => (
									<SelectItem key={item.value} value={item.value}>
										{item.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={filters.verification_status}
							onValueChange={handleVerificationStatusFilter}
							items={verificationStatusFilterItems}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="min-w-fit" align="end">
								{verificationStatusFilterItems.map((item) => (
									<SelectItem key={item.value} value={item.value}>
										{item.variant ? (
											<Badge variant={item.variant}>
												{item.icon && (
													<HugeiconsIcon
														icon={item.icon}
														strokeWidth={2}
														className="size-3"
													/>
												)}
												{item.label}
											</Badge>
										) : (
											item.label
										)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={filters.check_status}
							onValueChange={handleCheckStatusFilter}
							items={checkStatusFilterItems}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="min-w-fit" align="end">
								{checkStatusFilterItems.map((item) => (
									<SelectItem key={item.value} value={item.value}>
										{item.variant ? (
											<Badge variant={item.variant} className="gap-1">
												{item.icon && (
													<HugeiconsIcon
														icon={item.icon}
														strokeWidth={2}
														className="size-3"
													/>
												)}
												{item.label}
											</Badge>
										) : (
											item.label
										)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="rounded-xl border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Název / Jméno</TableHead>
									<TableHead>Typ</TableHead>
									<TableHead>IČO / RČ</TableHead>
									<TableHead>Sankce</TableHead>
									<TableHead>Identifikace</TableHead>
									<TableHead>Kontrola</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{clients.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={6}
											className="py-8 text-center text-muted-foreground"
										>
											Žádní klienti nenalezeni
										</TableCell>
									</TableRow>
								) : (
									clients.map((client) => {
										const hasMissingInfo = client.has_missing_info;
										return (
											<TableRow
												key={client.id}
												className={cn(
													"cursor-pointer",
													client.has_sanctioned_person &&
														"bg-destructive/10 hover:bg-destructive/15",
												)}
												onClick={() => router.visit(`/clients/${client.id}`)}
											>
												<TableCell className="flex items-center gap-2 font-medium">
													{client.display_name}
													{hasMissingInfo && (
														<Tooltip>
															<TooltipTrigger>
																<HugeiconsIcon
																	icon={AlertCircleIcon}
																	strokeWidth={2}
																	className="size-4 text-warning"
																/>
															</TooltipTrigger>
															<TooltipContent>
																<MissingInfoList
																	missingInfo={client.missing_info}
																/>
															</TooltipContent>
														</Tooltip>
													)}
												</TableCell>
												<TableCell>
													{CLIENTABLE_TYPE_LABELS[client.clientable_type]}
												</TableCell>
												<TableCell>
													{client.clientable_type === "IndividualClient"
														? formatBirthNumber(client.identifier) || "—"
														: client.identifier || "—"}
												</TableCell>
												<TableCell>
													<SanctionedBadge client={client} />
												</TableCell>
												<TableCell>
													<VerificationStatusBadge
														verifiedCount={client.verified_count}
														totalCount={client.total_count}
														expiringSoon={client.verification_expiring_soon}
														expired={client.verification_expired}
													/>
												</TableCell>
												<TableCell>
													<CheckStatusBadge
														checked={client.checked}
														checkExpiresAt={client.check_expires_at}
														checkExpiringSoon={client.check_expiring_soon}
													/>
												</TableCell>
											</TableRow>
										);
									})
								)}
							</TableBody>
						</Table>
					</div>
				</div>
			</Dashboard>
		</>
	);
}

const MISSING_INFO_LABELS = {
	missing_address: "adresa sídla",
	missing_ubo: "skutečný majitel",
	missing_statutory_member: "člen statutárního orgánu",
	persons_incomplete: "nekompletní osoby",
	missing_birth_number: "rodné číslo",
	missing_document: "doklad totožnosti",
	missing_person_address: "adresa bydliště",
	missing_birth_info: "údaje o narození",
	missing_citizenship: "státní občanství",
	missing_gender: "pohlaví",
	missing_aml_check: "AML kontrola (sankce/PEP)",
	document_expired: "dokument expiroval",
	document_expiring: "dokument brzy expiruje",
} satisfies Record<ClientsClient["missing_info"][number], string>;

function MissingInfoList({
	missingInfo,
}: {
	missingInfo: ClientsClient["missing_info"];
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className="font-medium">Chybějící údaje:</span>
			<ul className="list-inside list-disc">
				{missingInfo.map((info) => (
					<li key={info}>{MISSING_INFO_LABELS[info]}</li>
				))}
			</ul>
		</div>
	);
}

function SanctionedBadge({ client }: { client: ClientsClient }) {
	if (!client.has_sanctioned_person) {
		return <Badge variant="secondary">Ne</Badge>;
	}

	const sanctionedPersons = client.sanctioned_persons;

	if (
		client.clientable_type === "BusinessClient" &&
		sanctionedPersons.length > 0
	) {
		return (
			<Tooltip>
				<TooltipTrigger
					render={
						<Badge variant="destructive" className="cursor-default">
							Ano ({client.sanctioned_persons_count})
						</Badge>
					}
				/>
				<TooltipContent>
					<div className="flex flex-col gap-1">
						<span className="font-medium">Sankcionované osoby:</span>
						<ul className="list-inside list-disc">
							{sanctionedPersons.map((person) => (
								<li key={person.name}>{person.name}</li>
							))}
						</ul>
					</div>
				</TooltipContent>
			</Tooltip>
		);
	}

	return <Badge variant="destructive">Ano</Badge>;
}
